import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaPaperPlane, FaSearch, FaArrowLeft, FaSmile, FaLock, FaCrown,
  FaUserCircle, FaCircle, FaCheckDouble, FaCheck, FaComments
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';

// Format timestamp
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  const time = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  if (diffDays === 0) return time;
  if (diffDays === 1) return 'काल';
  if (diffDays < 7) return date.toLocaleDateString('mr-IN', { weekday: 'short' });
  return date.toLocaleDateString('mr-IN', { day: 'numeric', month: 'short' });
};

// Conversation Item
const ConversationItem = ({ conversation, active, onClick, currentUserId }) => {
  const otherUser = conversation.participants?.find(p => p._id !== currentUserId) || conversation.otherUser || conversation;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 ${
        active ? 'bg-[#8B0000]/10 border-l-4 border-l-[#8B0000]' : 'hover:bg-gray-50'
      }`}
    >
      <div className="relative flex-shrink-0">
        {otherUser?.photos?.[0] || otherUser?.profilePhoto ? (
          <img
            src={otherUser.photos?.[0] || otherUser.profilePhoto}
            alt=""
            className="w-12 h-12 rounded-full object-cover border-2 border-[#FFD700]"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#8B0000] to-[#DC143C] flex items-center justify-center border-2 border-[#FFD700]">
            <FaUserCircle className="text-white/60 text-xl" />
          </div>
        )}
        {otherUser?.isOnline && (
          <FaCircle className="absolute bottom-0 right-0 text-green-500 text-[10px] border-2 border-white rounded-full" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-gray-800 truncate">{otherUser?.name || 'User'}</h4>
          <span className="text-[10px] text-gray-400 flex-shrink-0">
            {formatTime(conversation.lastMessageAt || conversation.updatedAt)}
          </span>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-xs text-gray-500 truncate flex-1">
            {conversation.lastMessage?.text || conversation.lastMessage || 'Start a conversation...'}
          </p>
          {(conversation.unreadCount || 0) > 0 && (
            <span className="bg-[#DC143C] text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1 ml-2 flex-shrink-0">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// Message Bubble
const MessageBubble = ({ message, isSent }) => (
  <div className={`flex ${isSent ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm ${
      isSent
        ? 'bg-[#8B0000] text-white rounded-br-md'
        : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
    }`}>
      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.text || message.content}</p>
      <div className={`flex items-center justify-end gap-1 mt-1 ${isSent ? 'text-white/60' : 'text-gray-400'}`}>
        <span className="text-[10px]">
          {message.createdAt ? new Date(message.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
        </span>
        {isSent && (
          message.read
            ? <FaCheckDouble className="text-[10px] text-blue-300" />
            : <FaCheck className="text-[10px]" />
        )}
      </div>
    </div>
  </div>
);

// Date Separator
const DateSeparator = ({ date }) => (
  <div className="flex items-center justify-center my-4">
    <div className="bg-[#FFD700]/20 text-[#8B0000] text-xs px-3 py-1 rounded-full font-medium">
      {date}
    </div>
  </div>
);

// Premium Overlay
const PremiumOverlay = () => (
  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex items-center justify-center">
    <div className="text-center p-8 max-w-sm">
      <div className="w-20 h-20 bg-[#FFD700]/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <FaLock className="text-[#8B0000] text-3xl" />
      </div>
      <h3 className="text-xl font-bold text-[#8B0000] font-['Noto_Sans_Devanagari'] mb-2">
        चॅट सुविधा Premium सदस्यांसाठी
      </h3>
      <p className="text-gray-500 text-sm font-['Noto_Sans_Devanagari'] mb-6">
        तुमच्या जुळलेल्या व्यक्तीशी चॅट करण्यासाठी Premium सदस्यता घ्या
      </p>
      <Link
        to="/pricing"
        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B0000] to-[#DC143C] text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all font-['Noto_Sans_Devanagari']"
      >
        <FaCrown /> Premium अपग्रेड करा
      </Link>
    </div>
  </div>
);

const ChatPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, api, isPremium } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(userId || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(!!userId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation);
    }
  }, [activeConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (userId) {
      setActiveConversation(userId);
      setShowMobileChat(true);
    }
  }, [userId]);

  // Poll for new messages
  useEffect(() => {
    if (!activeConversation || !isPremium) return;
    const interval = setInterval(() => {
      fetchMessages(activeConversation, true);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeConversation, isPremium]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/chat/conversations');
      setConversations(response.data.conversations || response.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (recipientId, silent = false) => {
    if (!silent) setMessagesLoading(true);
    try {
      const response = await api.get(`/api/chat/messages/${recipientId}`);
      setMessages(response.data.messages || response.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation || sending) return;

    const text = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistic update
    const optimisticMsg = {
      _id: Date.now().toString(),
      text,
      sender: user?._id,
      createdAt: new Date().toISOString(),
      read: false
    };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await api.post('/api/chat/send', {
        recipientId: activeConversation,
        text
      });
      fetchMessages(activeConversation, true);
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.filter(m => m._id !== optimisticMsg._id));
      setNewMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleConversationClick = (convUserId) => {
    setActiveConversation(convUserId);
    setShowMobileChat(true);
    navigate(`/chat/${convUserId}`, { replace: true });
  };

  const handleBackToList = () => {
    setShowMobileChat(false);
    navigate('/chat', { replace: true });
  };

  const getActiveUser = () => {
    const conv = conversations.find(c => {
      const other = c.participants?.find(p => p._id !== user?._id) || c.otherUser;
      return other?._id === activeConversation;
    });
    if (conv) {
      return conv.participants?.find(p => p._id !== user?._id) || conv.otherUser;
    }
    return null;
  };

  const activeUser = getActiveUser();

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const other = conv.participants?.find(p => p._id !== user?._id) || conv.otherUser;
    return other?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = msg.createdAt
      ? new Date(msg.createdAt).toLocaleDateString('mr-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      : 'आज';
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  return (
    <div className="h-screen flex flex-col bg-[#FFF8F0]">
      <Navbar />

      <div className="flex-1 flex overflow-hidden relative">
        {!isPremium && <PremiumOverlay />}

        {/* Left Panel - Conversations */}
        <div className={`w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 ${
          showMobileChat ? 'hidden md:flex' : 'flex'
        }`}>
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="संभाषण शोधा..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 rounded-lg text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-0">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse border-b border-gray-50">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-2/3" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <FaComments className="text-4xl text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm font-['Noto_Sans_Devanagari']">
                  {searchQuery ? 'कोणतेही संभाषण सापडले नाही' : 'अजून कोणतेही संभाषण नाही'}
                </p>
                {!searchQuery && (
                  <Link
                    to="/matches"
                    className="inline-block mt-3 text-sm text-[#8B0000] hover:text-[#DC143C] font-medium"
                  >
                    मॅचेस पहा →
                  </Link>
                )}
              </div>
            ) : (
              filteredConversations.map((conv, idx) => {
                const otherUserId = conv.participants?.find(p => p._id !== user?._id)?._id || conv.otherUser?._id;
                return (
                  <ConversationItem
                    key={conv._id || idx}
                    conversation={conv}
                    active={activeConversation === otherUserId}
                    onClick={() => handleConversationClick(otherUserId)}
                    currentUserId={user?._id}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel - Chat Window */}
        <div className={`flex-1 flex flex-col bg-[#FFF8F0] ${
          !showMobileChat ? 'hidden md:flex' : 'flex'
        }`}>
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center gap-3 shadow-sm flex-shrink-0">
                <button
                  onClick={handleBackToList}
                  className="md:hidden text-[#8B0000] p-1"
                >
                  <FaArrowLeft />
                </button>

                <div className="relative">
                  {activeUser?.photos?.[0] || activeUser?.profilePhoto ? (
                    <img
                      src={activeUser.photos?.[0] || activeUser.profilePhoto}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border-2 border-[#FFD700]"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B0000] to-[#DC143C] flex items-center justify-center border-2 border-[#FFD700]">
                      <FaUserCircle className="text-white/60" />
                    </div>
                  )}
                  {activeUser?.isOnline && (
                    <FaCircle className="absolute bottom-0 right-0 text-green-500 text-[8px] border-2 border-white rounded-full" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-gray-800 truncate">{activeUser?.name || 'Chat'}</h3>
                  <p className="text-xs text-gray-500">
                    {activeUser?.isOnline ? (
                      <span className="text-green-600">Online</span>
                    ) : (
                      'Offline'
                    )}
                  </p>
                </div>

                <Link
                  to={`/profile/${activeConversation}`}
                  className="text-xs text-[#8B0000] border border-[#8B0000] px-3 py-1.5 rounded-lg hover:bg-[#8B0000] hover:text-white transition-colors"
                >
                  प्रोफाइल
                </Link>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto px-4 py-4" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(139,0,0,0.03) 0%, transparent 50%)' }}>
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-3 border-[#8B0000] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-20">
                    <FaComments className="text-4xl text-[#8B0000]/20 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm font-['Noto_Sans_Devanagari']">संभाषण सुरू करा</p>
                  </div>
                ) : (
                  Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date}>
                      <DateSeparator date={date} />
                      {msgs.map((msg, idx) => (
                        <MessageBubble
                          key={msg._id || idx}
                          message={msg}
                          isSent={msg.sender === user?._id || msg.senderId === user?._id}
                        />
                      ))}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSend} className="bg-white px-4 py-3 border-t border-gray-200 flex items-center gap-3 flex-shrink-0">
                <button type="button" className="text-gray-400 hover:text-[#FFD700] transition-colors p-1">
                  <FaSmile className="text-xl" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="संदेश टाइप करा..."
                  className="flex-1 bg-gray-50 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
                  disabled={!isPremium}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending || !isPremium}
                  className="bg-[#8B0000] text-white p-2.5 rounded-full hover:bg-[#DC143C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FaPaperPlane className="text-sm" />
                </button>
              </form>
            </>
          ) : (
            /* No conversation selected */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-[#8B0000]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaComments className="text-[#8B0000] text-4xl" />
                </div>
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Noto_Sans_Devanagari'] mb-2">
                  संभाषण निवडा
                </h3>
                <p className="text-gray-500 text-sm font-['Noto_Sans_Devanagari']">
                  डावीकडून संभाषण निवडा किंवा नवीन सुरू करा
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
